<template>
  <MainTemplate>
    <MainNavbar
      slot="header"
      is-back-arrow-enabled
      has-small-logo
      title="MATCH-STATISTICS"
    />
    <MainFooter slot="footer" />
    <div class="w-100">
      <transition
        name="fade"
        mode="out-in"
        :duration="{ enter: 1000, leave: 200 }"
      >
        <BaseLoading
          v-if="isMatchStatsLoading || !match.id"
          color="#fff"
        />
        <b-row
          v-else
          :class="$style.wrapper"
        >
          <b-col :cols="6">
            <BaseMatchDate :date="match.start" />
          </b-col>
          <b-col
            :cols="6"
            class="d-flex justify-content-end"
          >
            <BaseBadge :variant="matchBadgeVariant">
              {{ $t(`match.status.${match.status}`) }}
            </BaseBadge>
          </b-col>

          <b-col
            :cols="6"
            class="pt-3 d-flex justify-content-center align-items-center text-center"
          >
            <BaseTeamWrapper
              :image="match.team1.image_url"
              :name="match.team1.name"
            />
          </b-col>
          <b-col
            :cols="6"
            class="pt-3 d-flex justify-content-center align-items-center text-center"
          >
            <BaseTeamWrapper
              :image="match.team2.image_url"
              :name="match.team2.name"
            />
          </b-col>

          <b-col
            v-if="match.status === matchStatuses.upcoming"
            :cols="12"
            class="py-4"
          >
            <BaseCountdown :date="match.start" />
          </b-col>
          <template v-else>
            <b-col
              :cols="6"
              class="py-4"
            >
              <span
                :class="$style.scoreWrapper"
                class="mr-2"
              >
                {{ match.scoreTeam1 }}
              </span>
            </b-col>
            <b-col
              :cols="6"
              class="py-4"
            >
              <span
                :class="$style.scoreWrapper"
                class="mr-2"
              >
                {{ match.scoreTeam2 }}
              </span>
            </b-col>
          </template>

          <b-col
            :cols="12"
            class="p-0"
          >
            <MatchUpcoming
              v-if="match.status === matchStatuses.upcoming"
              :history="match.history"
              :team1id="match.team1.id"
              :team1name="match.team1.name"
              :team2id="match.team2.id"
              :team2name="match.team2.name"
            />
            <MatchLiveAndFinished
              v-if="match.status === matchStatuses.live || match.status === matchStatuses.finished"
              :team1name="match.team1.name"
              :team2name="match.team2.name"
              :athletes="athletes"
              :is-athletes-loading="isAthletesLoading"
            />
          </b-col>
        </b-row>
      </transition>
    </div>
  </MainTemplate>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { getMatchBatchClass } from '@/helpers/ui'
import { MATCH_STATUSES } from '@/store/modules/matches/matchDetail'
import BaseTeamWrapper from './Components/BaseTeamWrapper'
import BaseMatchDate from '@/components/Atoms/BaseMatchDate'
import BaseCountdown from '@/components/Atoms/BaseCountdown'
import MatchUpcoming from './Components/MatchUpcoming'
import MatchLiveAndFinished from './Components/MatchLiveAndFinished'
import BaseLoading from '@/components/Atoms/BaseLoading'
import BaseBadge from '@/components/Atoms/BaseBadge'
import MainTemplate from '@/layout/Templates/MainTemplate'
import MainNavbar from '@/layout/MainNavbar'
import MainFooter from '@/layout/MainFooter'

export default {
  components: {
    MainFooter,
    MainNavbar,
    MainTemplate,
    BaseBadge,
    BaseLoading,
    MatchUpcoming,
    MatchLiveAndFinished,
    BaseCountdown,
    BaseMatchDate,
    BaseTeamWrapper,
  },
  metaInfo() {
    return {
      title: this.$t('page-titles.match-detail', {
        team1: this.match.team1 ? this.match.team1.name[this.$i18n.locale] : '',
        team2: this.match.team2 ? this.match.team2.name[this.$i18n.locale] : '',
      }),
    }
  },
  data() {
    return {
      matchId: `${this.$route.params.league}/${this.$route.params.match}`,
      matchStatuses: MATCH_STATUSES,
      roundId: null,
    }
  },
  computed: {
    ...mapState({
      isMatchStatsLoading: state => state['matches/stats'].isMatchStatsLoading,
      isAthletesLoading: state => state['rounds/assignAthleteIntoRound'].isAthletesLoading,
    }),
    athletes() {
      return this.$store.getters['rounds/athletesInRound/getSelectedAthletesForRound']()
    },
    match() {
      return this.$store.getters['matches/stats/getMatchStats'](this.matchId)
    },
    matchBadgeVariant() {
      return getMatchBatchClass(this.match.status)
    },
  },
  created() {
    this.fetchMatchStats({
      matchId: this.matchId,
      // eslint-disable-next-line camelcase
    }).then(({ round_id }) => {
      this.fetchSelectedAthletes({ roundId: round_id })
      if ([MATCH_STATUSES.live, MATCH_STATUSES.finished].includes(this.match.status)) {
        if (this.$route.name === 'match-current-standings') {
          this.fetchScoreCounts({ roundId: round_id }).then((ranks) => {
            ranks.map(({ score }) => this.fetchPlayersWithScore({ roundId: round_id, score }))
          })
        } else {
          this.fetchMatchAthletesPerformance({ matchId: this.matchId })
        }
      } else if (this.match.status === MATCH_STATUSES.upcoming) {
        this.fetchTeamTopScorers({
          teamId: this.match.team1.id,
        })
        this.fetchTeamTopScorers({
          teamId: this.match.team2.id,
        })
      }
    })
  },
  methods: {
    ...mapActions({
      fetchMatchStats: 'matches/stats/fetchMatchStats',
      fetchSelectedAthletes: 'rounds/athletesInRound/fetchSelectedAthletes',
      fetchScoreCounts: 'rounds/scoreCounts/fetchScoreCounts',
      fetchPlayersWithScore: 'rounds/scoreCounts/fetchPlayersWithScore',
      fetchMatchAthletesPerformance: 'matches/matchAthletePerformance/fetchMatchAthletesPerformance',
      fetchTeamTopScorers: 'teams/topScorers/fetchTeamTopScorers',
    }),
  },
}
</script>

<style lang="scss" module>
  @import '../../assets/scss/variables';
  .wrapper {
    background-color: $color-bleached-silk;
    padding: 1rem 1rem 0 1rem;
  }
  .scoreWrapper {
    background-color: $color-white;
    color: $color-midnight-blue;
    font-size: $font-size-xxlarge;
    font-weight: 400;
    border-radius: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: .75rem 1.75rem;
  }
</style>
